'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// Custom SaaS development: pick what you're building and watch it go from
// scope to launch, with the production checks the page promises (Supabase
// auth, row-level security, Vercel). The bars compare an AI-first build with
// a traditional one for the same product: the page's "3–5x faster", made
// concrete. Example timelines, labelled as such.

const PRODUCTS = {
  portal: { label: 'Client portal', fast: 4, slow: 14 },
  booking: { label: 'Booking SaaS', fast: 6, slow: 20 },
  internal: { label: 'Internal tool', fast: 3, slow: 10 },
}

const PHASES = (weeks) => [
  { at: 1, text: 'Scope, screens and architecture agreed' },
  { at: 2, text: 'Database and login on Supabase' },
  { at: 3, text: 'Core features built with Claude Code' },
  { at: 4, text: 'Security review: row-level security on' },
  { at: 5, text: `Launched on Vercel in week ${weeks}` },
]
const DELAYS = [500, 1000, 1000, 1000, 1000, 600]

export default function BuildTimeline() {
  const [ref, onScreen] = useOnScreen()
  const [product, setProduct] = useState('portal')
  const [run, setRun] = useState(0)
  const p = PRODUCTS[product]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${product}-${run}` })
  const finished = step >= DELAYS.length
  const pick = (key) => { setProduct(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-build"
      title="From idea to live product"
      label="Demo: building a SaaS product AI-first, phase by phase, compared with a traditional build"
      tabs={Object.entries(PRODUCTS).map(([key, v]) => ({ key, label: v.label }))}
      activeTab={product}
      onTab={pick}
      onReplay={() => pick(product)}
    >
      <div className="svd-race" aria-label={`AI-first build about ${p.fast} weeks, traditional build about ${p.slow} weeks`}>
        <div className="svd-race-row">
          <span>AI-first with GHL Prime</span>
          <span className="svd-race-track"><motion.i key={`f-${product}-${run}`} className="is-fast" initial={{ width: 0 }} animate={{ width: onScreen ? `${(p.fast / p.slow) * 100}%` : 0 }} transition={{ duration: 1, ease: 'easeOut' }} /></span>
          <b>{p.fast} wks</b>
        </div>
        <div className="svd-race-row">
          <span>Traditional build</span>
          <span className="svd-race-track"><motion.i key={`s-${product}-${run}`} initial={{ width: 0 }} animate={{ width: onScreen ? '100%' : 0 }} transition={{ duration: 2.4, ease: 'linear' }} /></span>
          <b>{p.slow} wks</b>
        </div>
      </div>
      <Trail title="Build progress" items={PHASES(p.fast)} step={step} />
      <p className="svd-footnote">Example timelines. Your real plan is set on the scoping call.</p>
      <ResultStamp show={finished} tone="good" text="Live · production-ready" id={`${product}-${run}`} />
    </DemoFrame>
  )
}
